/**
 * L'état d'UNE fiche — architecture H.
 *
 * Il n'y a qu'un seul objet de travail. Pas de collection de « propositions »
 * à côté, pas de panier, pas d'étape « ajouter à ma fiche » : ce que le moteur
 * compose EST la fiche, et les ajustements la modifient en place.
 *
 * C'est la différence de fond avec `SelectionContext`, qui reste intact pour
 * l'ancien parcours : celui-ci porte une liste d'exercices retenus, celui-là
 * porte un document qui sait de quel objectif il répond.
 *
 * La reprise locale est volontairement séparée (`lemaitremot_fiche_h`) : les
 * deux parcours coexistent tant que H n'est pas validé, et ils ne doivent pas
 * pouvoir s'abîmer l'un l'autre.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1/fiches`;
const STOCKAGE = "lemaitremot_fiche_h";

const jeton = () => localStorage.getItem("lemaitremot_session_token") || "";
const entetes = () => (jeton() ? { "X-Session-Token": jeton() } : {});

/**
 * Une empreinte courte et déterministe (FNV-1a 32 bits).
 *
 * Elle ne protège rien : elle DISTINGUE. On s'en sert pour dire « ce document
 * n'est plus celui qui a été enregistré » et pour cloisonner un brouillon par
 * compte sans recopier le jeton de session sous une clé lisible.
 */
function digeste(texte) {
  let h = 0x811c9dc5;
  for (let i = 0; i < texte.length; i += 1) {
    h ^= texte.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

/**
 * LE BROUILLON EST CLOISONNÉ PAR COMPTE.
 *
 * Une seule clé pour tout le navigateur, et deux comptes sur le même poste —
 * une salle des profs, un ordinateur partagé — se passaient la fiche de
 * l'autre. Pire : le brouillon porte le `sheet_uid`, donc le second pouvait
 * ENREGISTRER par-dessus la fiche du premier.
 *
 * Le jeton lui-même n'entre pas dans la clé : un secret recopié à un endroit
 * de plus est un secret de plus à perdre. Son empreinte suffit à séparer.
 */
export function clefDeBrouillon(jetonSession) {
  return jetonSession ? `${STOCKAGE}:${digeste(jetonSession)}` : STOCKAGE;
}

/**
 * L'EMPREINTE DU DOCUMENT COURANT.
 *
 * `version_figee` était écrite à la composition et ne bougeait plus : retirer
 * un exercice, en renouveler un, réordonner — elle continuait d'annoncer la
 * composition d'origine. Un champ dont le nom promet d'identifier le document
 * et qui en identifie un autre est pire qu'un champ absent.
 *
 * Chaque exercice porte un identifiant propre, émis par le serveur et
 * renouvelé à chaque tirage : la suite ORDONNÉE de ces identifiants désigne
 * donc exactement ce document-ci.
 */
export function empreinteDuDocument(exercices, titre = "") {
  const corps = (exercices || []).map((ex, i) => `${i}:${ex.id_exercice}`).join("|");
  // LE TITRE EN FAIT PARTIE. Il n'est pas un ornement : c'est ce que
  // l'utilisateur lit dans « Mes fiches » et ce que le PDF imprime. Le laisser
  // dehors, c'est renommer une fiche enregistrée, la voir annoncée « à jour »,
  // puis la perdre en changeant d'objectif sans le moindre avertissement.
  return digeste(`${titre || ""}\u0000${corps}`);
}

/** Lit le brouillon local, sans jamais faire échouer le démarrage. */
function lireBrouillon(clef) {
  try {
    const brut = localStorage.getItem(clef);
    if (!brut) return null;
    const objet = JSON.parse(brut);
    if (!objet?.fiche || !Array.isArray(objet?.exercices)) return null;
    return objet;
  } catch (e) {
    try { localStorage.removeItem(clef); } catch (_) { /* stockage indisponible */ }
    return null;
  }
}

export function useFicheH() {
  const [fiche, setFiche] = useState(null);
  const [exercices, setExercices] = useState([]);
  const [questionsExhaustives, setQuestionsExhaustives] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [positionOccupee, setPositionOccupee] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [dernierRetrait, setDernierRetrait] = useState(null);
  const [erreurEnregistrement, setErreurEnregistrement] = useState(null);
  const [enregistrementEnCours, setEnregistrementEnCours] = useState(false);
  const [stockageLocalOK, setStockageLocalOK] = useState(true);
  const sauvegardeRef = useRef(null);
  const generationDocument = useRef(0);
  /**
   * LE BROUILLON A-T-IL ÉTÉ RELU ?
   *
   * Le retour depuis l'authentification consomme l'intention rangée en
   * session. Or le brouillon, lui, est relu par un effet de montage. Si
   * l'intention était consommée pendant que la fiche est encore nulle, la
   * sauvegarde sortait par sa garde `if (!fiche)` — et l'intention,
   * consommée, n'existait plus pour être rejouée. L'utilisateur s'inscrit
   * parce qu'on lui a promis de retrouver sa fiche, revient, et sa fiche
   * n'est nulle part.
   *
   * Ce drapeau dit « la lecture locale est finie » — que le brouillon ait
   * été trouvé ou non. Personne ne doit consommer d'intention avant lui.
   */
  const [brouillonRestaure, setBrouillonRestaure] = useState(false);
  /** Le service a refermé ce parcours. Ce n'est pas une erreur à réessayer. */
  const [parcoursFerme, setParcoursFerme] = useState(false);
  /* `rouvrir` doit lire l'état COURANT sans entrer dans ses dépendances : une
   * fonction recréée à chaque frappe relancerait l'effet de reprise de la
   * page. Des refs tenues à jour donnent la lecture sans la dépendance. */
  const ficheRef = useRef(null);
  const exercicesRef = useRef([]);
  const premierRendu = useRef(true);

  useEffect(() => { ficheRef.current = fiche; }, [fiche]);
  useEffect(() => { exercicesRef.current = exercices; }, [exercices]);

  // Reprise : la fiche en cours revient telle qu'on l'a laissée.
  useEffect(() => {
    const clef = clefDeBrouillon(jeton());
    let brouillon = lireBrouillon(clef);

    // LA FICHE COMPOSÉE SANS COMPTE SUIT CELUI QUI S'INSCRIT.
    //
    // C'est la promesse même de H : composer d'abord, s'inscrire ensuite. Le
    // brouillon d'invité est donc ADOPTÉ par le compte qui arrive — puis
    // retiré de la case commune, faute de quoi le compte suivant sur le même
    // poste en hériterait à son tour.
    if (!brouillon && clef !== STOCKAGE) {
      brouillon = lireBrouillon(STOCKAGE);
      if (brouillon) {
        try { localStorage.removeItem(STOCKAGE); } catch (_) { /* rien à faire */ }
      }
    }

    if (brouillon) {
      setFiche(brouillon.fiche);
      setExercices(brouillon.exercices);
      setQuestionsExhaustives(brouillon.questionsExhaustives !== false);
    }
    premierRendu.current = false;
    setBrouillonRestaure(true);
  }, []);

  // Persistance : à chaque changement, et seulement après le premier rendu —
  // sinon l'état vide initial effacerait le brouillon qu'on vient de lire.
  // La clé est relue à chaque écriture : se connecter change de case sans
  // qu'on ait à remonter le composant.
  useEffect(() => {
    if (premierRendu.current) return;
    const clef = clefDeBrouillon(jeton());
    try {
      if (fiche) {
        localStorage.setItem(
          clef, JSON.stringify({ fiche, exercices, questionsExhaustives })
        );
      } else {
        localStorage.removeItem(clef);
      }
      setStockageLocalOK(true);
    } catch (e) { setStockageLocalOK(false); }
  }, [fiche, exercices, questionsExhaustives]);

  /** Un objectif -> une fiche, en un appel. */
  const composer = useCallback(async (objectifId, options = {}) => {
    setEnCours(true);
    setErreur(null);
    try {
      const { data } = await axios.post(`${API}/composer`, {
        ...(Array.isArray(objectifId) ? { objectif_ids: objectifId } : { objectif_id: objectifId }),
        longueur: options.longueur,
        graine: options.graine,
        titre: options.titre,
        formes_reprises: options.formesReprises,
      }, { headers: entetes() });
      // LE JETON DE FICHE, ÉMIS PAR LE SERVEUR.
      // Il identifie CETTE fiche pour CETTE visite et ne bouge pas quand on
      // l'ajuste : c'est ce qui permet de prendre le corrigé après avoir
      // renouvelé un exercice, sans se voir refuser pour « autre fiche ».
      if (options.ajouter && ficheRef.current) {
        const courant = ficheRef.current;
        if (courant.niveau !== data.fiche.niveau || exercicesRef.current.length + data.exercices.length > 12) {
          setErreur("La fiche est limitée à 12 exercices d'une même classe. Votre travail est inchangé.");
          return null;
        }
        setFiche(f => ({ ...f, objectifs: [...new Map([...(f.objectifs || [f]), ...(data.fiche.objectifs || [data.fiche])].map(o => [o.objectif_id, o])).values()] }));
        setExercices(liste => [...liste, ...data.exercices]);
      } else {
        generationDocument.current += 1;
        setFiche({ ...data.fiche, jeton: data.fiche_jeton || null });
        setExercices(data.exercices);
        setErreurEnregistrement(null);
      }
      setQuestionsExhaustives(data.questions_exhaustives !== false);
      return data;
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      // UN PARCOURS FERMÉ N'EST PAS UNE PANNE PASSAGÈRE.
      //
      // Tout ce qui n'était pas « composition impossible » devenait « La fiche
      // n'a pas pu être créée. Réessayez dans un instant. » Avec
      // `ARCHITECTURE_H=0`, cette phrase s'affichait PAR-DESSUS l'écran qui
      // dit, lui, que le parcours n'est pas ouvert : deux affirmations
      // contradictoires, dont une fausse, et une invitation à réessayer
      // indéfiniment quelque chose qui ne peut pas marcher.
      if (code === "FICHE_H_FERMEE") {
        setParcoursFerme(true);
        return null;
      }
      setErreur(
        code === "COMPOSITION_IMPOSSIBLE"
          ? "Cet objectif n'est pas disponible en ce moment."
          : "La fiche n'a pas pu être créée. Réessayez dans un instant."
      );
      return null;
    } finally {
      setEnCours(false);
    }
  }, []);

  /**
   * D'autres nombres pour UNE position — la forme ne change pas.
   *
   * `remplacerPar` permet de changer de forme sans changer de position :
   * c'est « Remplacer », et le reste de la fiche ne bouge pas.
   */
  const renouveler = useCallback(async (index, remplacerPar = null) => {
    if (!fiche) return;
    const cible = exercices[index];
    if (!cible) return;
    // L'IDENTITÉ, PAS LA POSITION.
    //
    // La réponse était appliquée à l'INDEX demandé. Reproduit : « Autres
    // nombres » sur A, l'utilisateur déplace A en dernier pendant la requête,
    // la réponse arrive et écrase la position 0 — c'est-à-dire B. Le travail
    // de l'utilisateur était remplacé sans un mot.
    //
    // On retient donc l'identifiant de l'exercice visé, et on l'applique là où
    // il se trouve AU MOMENT DE LA RÉPONSE. S'il n'y est plus (retiré entre
    // temps), la réponse est jetée : la faire réapparaître serait pire.
    const cibleId = cible.id_exercice;
    setPositionOccupee(cibleId);
    try {
      const { data } = await axios.post(`${API}/renouveler`, {
        objectif_id: cible.metadata?.objectif_id || fiche.objectif_id,
        forme_libelle: remplacerPar || cible.forme_libelle,
        // Le serveur retire jusqu'à obtenir autre chose : changer de graine
        // ne suffit pas à changer de contenu, et un clic qui ne change rien
        // passe pour un bouton cassé.
        eviter_html: remplacerPar ? null : cible.enonce_html,
      }, { headers: entetes() });
      setExercices((liste) => {
        const rang = liste.findIndex((ex) => ex.id_exercice === cibleId);
        if (rang === -1) return liste;      // retiré pendant la requête
        const copie = [...liste];
        copie[rang] = data.exercice;
        return copie;
      });
      return data.exercice;
    } catch (e) {
      setErreur("Cet exercice n'a pas pu être renouvelé.");
      return null;
    } finally {
      setPositionOccupee((occupee) => (occupee === cibleId ? null : occupee));
    }
  }, [fiche, exercices]);

  /**
   * Un exercice de plus — LA RECETTE EST DÉCIDÉE PAR LE SERVEUR.
   *
   * Le navigateur reconstruisait sa propre alternance
   * (`formes[longueur % nombre_de_formes]`), ce qui n'est PAS la séquence de
   * la recette : sur « comparer, comparer, ranger », il servait « comparer,
   * ranger, comparer, ranger… ». Deux définitions de la même recette, dont une
   * inventée par l'interface.
   *
   * On envoie donc le RANG, et le serveur répond avec la forme que sa recette
   * place à ce rang — et refuse au-delà de la borne qu'elle déclare.
   */
  const allonger = useCallback(async (objectifId = null) => {
    if (!fiche) return;
    if (exercices.length >= 12) { setErreur("La fiche est limitée à 12 exercices."); return; }
    const objectif = typeof objectifId === "string" ? objectifId : exercices.at(-1)?.metadata?.objectif_id || fiche.objectif_id;
    const rang = exercices.filter(ex => (ex.metadata?.objectif_id || fiche.objectif_id) === objectif).length;
    setPositionOccupee(`ajout-${rang}`);
    try {
      const { data } = await axios.post(`${API}/allonger`, {
        objectif_id: objectif,
        rang,
      }, { headers: entetes() });
      setExercices((liste) => [...liste, data.exercice]);
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      setErreur(
        code === "LONGUEUR_MAX"
          ? "Cette fiche a atteint la longueur prévue pour cet objectif."
          : "Impossible d'ajouter un exercice."
      );
    } finally {
      setPositionOccupee(null);
    }
  }, [fiche, exercices]);

  const deplacer = useCallback((de, vers) => {
    setExercices((liste) => {
      if (vers < 0 || vers >= liste.length) return liste;
      const copie = [...liste];
      const [pris] = copie.splice(de, 1);
      copie.splice(vers, 0, pris);
      return copie;
    });
  }, []);

  /** Retirer, avec de quoi annuler : un retrait ne doit jamais être définitif. */
  const retirer = useCallback((index) => {
    setExercices((liste) => {
      const exercice = liste[index];
      if (!exercice) return liste;
      setDernierRetrait({ exercice, index });
      return liste.filter((_, i) => i !== index);
    });
  }, []);

  const annulerRetrait = useCallback(() => {
    setDernierRetrait((retrait) => {
      if (!retrait) return null;
      setExercices((liste) => {
        if (retrait.exercices) {
          const copie = [...liste];
          retrait.exercices.forEach(({ exercice, index }) => {
            if (!copie.some(ex => ex.id_exercice === exercice.id_exercice)) copie.splice(Math.min(index, copie.length), 0, exercice);
          });
          return copie;
        }
        if (liste.some((ex) => ex.id_exercice === retrait.exercice.id_exercice)) return liste;
        const copie = [...liste];
        copie.splice(Math.min(retrait.index, copie.length), 0, retrait.exercice);
        return copie;
      });
      return null;
    });
  }, []);

  const retirerObjectif = useCallback((id) => {
    setExercices(liste => {
      const retraits = liste.map((exercice, index) => ({ exercice, index }))
        .filter(({ exercice }) => (exercice.metadata?.objectif_id || ficheRef.current?.objectif_id) === id);
      if (retraits.length === liste.length) return liste;
      setDernierRetrait({ exercices: retraits });
      return liste.filter(ex => !retraits.some(r => r.exercice.id_exercice === ex.id_exercice));
    });
  }, []);

  const renommer = useCallback((titre) => {
    setFiche((f) => (f ? { ...f, titre } : f));
  }, []);

  /**
   * Conserver la fiche dans le compte.
   *
   * On réutilise `create-from-selection`, la route qui verse déjà les
   * exercices dans la bibliothèque PUIS crée la fiche : dupliquer ce chemin
   * ferait diverger deux façons d'enregistrer le même objet.
   *
   * `etat.sheet_uid` mémorise l'identifiant : un deuxième « Enregistrer » MET
   * À JOUR la fiche au lieu d'en empiler une copie — c'est exactement le
   * défaut que l'atelier a déjà connu, deux fiches identiques à une heure et
   * demie d'intervalle dans le compte d'un utilisateur.
   */
  const enregistrer = useCallback(async (jetonFourni = null, options = {}) => {
    if (!fiche || !exercices.length) return null;
    const token = jetonFourni || jeton();
    if (!token) return null;
    if (sauvegardeRef.current) return sauvegardeRef.current;
    const generation = generationDocument.current;
    setEnregistrementEnCours(true);
    setErreurEnregistrement(null);
    const operation = (async () => {

    const corps = {
      title: fiche.titre,
      description: `${(fiche.objectifs || [fiche]).filter(o => exercices.some(ex => (ex.metadata?.objectif_id || fiche.objectif_id) === o.objectif_id)).map(o => o.objectif_libelle).join(" · ")} — ${exercices.length} exercice(s)`,
      // LA PROVENANCE VOYAGE AVEC LA FICHE, pour qu'elle se rouvre ici et pas
      // dans l'éditeur historique.
      parcours: "H",
      objectif_id: fiche.objectif_id,
      exercises: exercices.map((ex, i) => ({
        exercise_uid: ex.id_exercice,
        order: i + 1,
        generator_key: ex.metadata?.generator_key || null,
        code_officiel: ex.metadata?.code_officiel || (fiche.objectifs || []).find(o => o.objectif_id === ex.metadata?.objectif_id)?.code_officiel || fiche.code_officiel || "",
        difficulty: ex.metadata?.difficulte || "moyen",
        seed: ex.metadata?.seed_used ?? ex.metadata?.seed ?? null,
        variables: ex.variables || {},
        enonce_html: ex.enonce_html || "",
        solution_html: ex.solution_html || "",
        metadata: {
          niveau: fiche.niveau,
          chapitre: fiche.objectif_libelle,
          objectif_id: fiche.objectif_id,
          forme: ex.forme_libelle,
          ...(ex.metadata || {}),
        },
      })),
    };

    const entete = { "Content-Type": "application/json", "X-Session-Token": token };
    const uid = options.copie ? null : fiche.etat?.sheet_uid;
    let reponse;
    if (uid) {
      /* METTRE À JOUR EXIGE QUE LES EXERCICES EXISTENT DÉJÀ.
       *
       * `PUT /user/sheets/{uid}` refuse (400) toute référence absente de la
       * bibliothèque, alors que la création, elle, verse les exercices au
       * passage. Un exercice ajouté APRÈS le premier enregistrement faisait
       * donc échouer la mise à jour en silence : la fiche conservée restait à
       * trois exercices pendant que l'écran en montrait quatre. On les verse
       * d'abord, avec la route d'import qui sert déjà à cela. */
      await fetch(`${BACKEND_URL}/api/user/exercises/import-batch`, {
        method: "POST", headers: entete,
        body: JSON.stringify({ exercises: corps.exercises }),
      }).catch(() => { /* la mise à jour dira si quelque chose manque */ });

      // La révision lue lors du dernier enregistrement. Le serveur la met dans
      // le filtre de son écriture : si la fiche a bougé ailleurs, il refuse
      // (409) au lieu d'écraser une version plus récente.
      reponse = await fetch(`${BACKEND_URL}/api/user/sheets/${uid}`, {
        method: "PUT", headers: entete,
        body: JSON.stringify({ ...corps, revision: fiche.etat?.revision ?? 1 }),
      });
      // La fiche a pu être supprimée depuis « Mes fiches » : on retombe alors
      // sur une création plutôt que d'échouer ou d'écrire dans le vide.
      if (reponse.status === 404) reponse = null;
    }
    // L'IDENTIFIANT MORT NE DOIT PAS SURVIVRE À SA FICHE.
    //
    // Le repli créait bien une nouvelle fiche, mais l'état gardait ensuite
    // l'ANCIEN `sheet_uid` — celui qu'on venait de constater disparu. Tout
    // enregistrement suivant repartait donc sur un identifiant mort, et la
    // révision retenue n'était celle d'aucune fiche existante.
    const recree = Boolean(uid) && !reponse;
    if (!reponse) {
      reponse = await fetch(`${BACKEND_URL}/api/user/sheets/create-from-selection`, {
        method: "POST", headers: entete, body: JSON.stringify(corps),
      });
    }
    const resultat = await reponse.json().catch(() => ({}));
    if (!reponse.ok) {
      const code = resultat?.detail?.code;
      setErreurEnregistrement(code === "SHEET_REVISION_CONFLICT" || code === "SHEET_REVISION_REQUIRED" ? "conflit" : "echec");
      setErreur(
        code === "SHEET_REVISION_CONFLICT" || code === "SHEET_REVISION_REQUIRED"
          ? "Cette fiche a été modifiée ailleurs. Rouvrez-la depuis « Mes fiches »."
          : "La fiche n'a pas pu être conservée."
      );
      return null;
    }
    const identifiant = (recree ? resultat.sheet_uid : uid) || resultat.sheet_uid;
    setErreur(null);
    const etatSauve = {
        local: true,
        sheet_uid: identifiant,
        // CE QUI A ÉTÉ ENREGISTRÉ, pour savoir ensuite ce qui a bougé.
        empreinte: empreinteDuDocument(exercices, fiche.titre),
        // On RETIENT la révision RENDUE PAR LE SERVEUR : sans elle, le
        // deuxième enregistrement repartirait avec une révision périmée et
        // se ferait refuser pour un conflit qui n'existe pas. Le repli
        // n'existe que pour un serveur antérieur à ce champ.
        revision: recree
          ? (resultat.revision ?? 1)
          : (resultat.revision ?? ((fiche.etat?.revision ?? 0) + 1)),
        enregistree_le: new Date().toISOString(),
    };
    // Une navigation peut démonter l'éditeur pendant l'écriture. La preuve
    // de sauvegarde rejoint alors aussi SON brouillon, jamais une autre fiche.
    try {
      const clef = clefDeBrouillon(token);
      const local = lireBrouillon(clef);
      const memeDocument = (fiche.jeton && local?.fiche?.jeton === fiche.jeton)
        || (uid && local?.fiche?.etat?.sheet_uid === uid);
      if (local && memeDocument && (local.fiche.etat?.revision || 0) <= etatSauve.revision) {
        local.fiche.etat = etatSauve;
        localStorage.setItem(clef, JSON.stringify(local));
      }
    } catch (_) { /* L'état affiché reste vrai ; le stockage local est séparé. */ }
    setFiche((f) => f && generationDocument.current === generation ? { ...f, etat: etatSauve } : f);
    return identifiant;
    })();
    sauvegardeRef.current = operation;
    try { return await operation; }
    catch (_) { setErreurEnregistrement("echec"); return null; }
    finally { sauvegardeRef.current = null; setEnregistrementEnCours(false); }
  }, [fiche, exercices]);

  /**
   * ROUVRIR UNE FICHE DU COMPTE, DANS H.
   *
   * « Mes fiches » envoyait toute fiche vers l'éditeur historique, y compris
   * celles composées ici : l'utilisateur repartait d'un écran qu'il n'avait
   * jamais vu, sans son objectif ni ses formes.
   *
   * Deux lectures, et aucune composition : le DOCUMENT vient du compte
   * (`complet=1` joint les énoncés aux références), le CONTRAT vient du
   * catalogue. Passer par `composer` tirerait de nouveaux exercices — ce qui
   * remplacerait la fiche qu'on voulait rouvrir.
   */
  const rouvrir = useCallback(async (sheetUid, options = {}) => {
    /* UNE REPRISE EXPLICITE GAGNE — MAIS PAS EN SILENCE.
     *
     * La page court-circuitait la reprise dès que le brouillon portait le même
     * identifiant : après « Cette fiche a été modifiée ailleurs, rouvrez-la
     * depuis Mes fiches », le seul geste proposé ne faisait rien.
     *
     * Elle va donc toujours chercher le serveur. Reste le cas où le brouillon
     * porte du travail NON ENREGISTRÉ sur cette même fiche : le remplacer sans
     * demander serait échanger un défaut contre un pire. */
    const memeFiche = ficheRef.current?.etat?.sheet_uid === sheetUid;
    // IL FAUT UNE PREUVE, PAS UN DOUTE. « Du travail est en cours » se
    // constate : une empreinte d'enregistrement existe, et le document ne lui
    // correspond plus. Sans empreinte, on ne sait rien — et une demande
    // explicite ne doit pas être bloquée par une ignorance.
    const empreinteEnregistree = ficheRef.current?.etat?.empreinte;
    const aPerdre = Boolean(ficheRef.current && exercicesRef.current.length) && (
      (Boolean(empreinteEnregistree) && empreinteEnregistree !== empreinteDuDocument(exercicesRef.current, ficheRef.current?.titre))
      || (!memeFiche && !ficheRef.current?.etat?.sheet_uid)
    );
    if (aPerdre && !options.confirme) {
      return { refuse: "TRAVAIL_NON_ENREGISTRE" };
    }

    setEnCours(true);
    setErreur(null);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/user/sheets/${sheetUid}?complet=1`,
        { headers: entetes() }
      );

      const repris = (data.exercises || []).map((e) => ({
        id_exercice: e.exercise_uid,
        forme_libelle: e.metadata?.forme || "",
        enonce_html: e.enonce_html || "",
        solution_html: e.solution_html || "",
        figure_svg_enonce: e.metadata?.figure_svg_enonce || null,
        figure_svg_solution: e.metadata?.figure_svg_solution || null,
        variables: e.variables || {},
        metadata: e.metadata || {},
      }));

      const objectifId = data.objectif_id || repris[0]?.metadata?.objectif_id || null;
      const ids = [...new Set(repris.map(ex => ex.metadata?.objectif_id || objectifId).filter(Boolean))];
      // Le contrat est un PLUS : il rend les formes qu'on a le droit d'ajouter
      // et la justification de la recette. S'il manque, la fiche s'ouvre quand
      // même — un document qu'on ne peut plus relire du tout serait un bien
      // plus grand dommage qu'un menu « Remplacer » vide.
      let contrat = {};
      if (objectifId) {
        try {
          const reponse = await axios.get(
            `${API}/objectifs/${objectifId}`, { headers: entetes() }
          );
          contrat = reponse?.data || {};
        } catch (erreurContrat) {
          // H REFERMÉE : on n'ouvre pas un demi-éditeur. Un document sans ses
          // formes autorisées, dans un parcours qui n'accepte plus rien, est
          // exactement l'ambiguïté qu'un drapeau d'activation doit éviter.
          // « Mes fiches » envoie d'ailleurs ces fiches vers l'éditeur
          // historique tant que la porte est close ; on n'arrive ici que par
          // un lien direct.
          if (erreurContrat?.response?.data?.detail?.code === "FICHE_H_FERMEE") {
            setParcoursFerme(true);
            return null;
          }
          /* catalogue indisponible : on rouvre sans le contrat */
        }
      }

      const contrats = await Promise.all(ids.map(async id => {
        if (id === objectifId) return { ...contrat, objectif_id: id };
        try { return (await axios.get(`${API}/objectifs/${id}`, { headers: entetes() })).data; }
        catch (_) { return { objectif_id: id, objectif_libelle: repris.find(ex => ex.metadata?.objectif_id === id)?.metadata?.objectif_libelle || "Objectif indisponible", formes_autorisees: [] }; }
      }));
      generationDocument.current += 1;
      setErreurEnregistrement(null);

      setFiche({
        ...contrat,
        objectifs: contrats,
        version_contrat: 2,
        objectif_id: objectifId,
        titre: data.title,
        // ROUVERTE = À JOUR. On vient de lire le document tel qu'il est
        // enregistré : son empreinte est, par construction, celle de
        // l'enregistré.
        etat: {
          local: true,
          sheet_uid: data.sheet_uid,
          revision: data.revision ?? 1,
          empreinte: empreinteDuDocument(repris, data.title),
          enregistree_le: data.updated_at || new Date().toISOString(),
        },
        version_figee: { graine: null, formes_tirees: [] },
        ordre: repris.map((e) => e.id_exercice),
      });
      setExercices(repris);
      setQuestionsExhaustives(true);
      return data.sheet_uid;
    } catch (e) {
      setErreur(
        e?.response?.status === 404
          ? "Cette fiche n'existe plus dans votre compte."
          : "Cette fiche n'a pas pu être rouverte."
      );
      return null;
    } finally {
      setEnCours(false);
    }
  }, []);

  /**
   * L'EMPREINTE DU DOCUMENT AFFICHÉ, RECALCULÉE À CHAQUE CHANGEMENT.
   *
   * Elle est exposée DANS `version_figee`, à la place où l'on cherche
   * l'identité d'une version. `graine` et `formes_tirees` restent à côté :
   * elles disent d'où vient la fiche, pas ce qu'elle est devenue.
   */
  const empreinte = useMemo(
    () => empreinteDuDocument(exercices, fiche?.titre),
    [exercices, fiche?.titre]
  );

  const ficheVue = useMemo(
    () => (fiche
      ? { ...fiche, objectifs: (fiche.objectifs || [fiche]).filter(o => exercices.some(ex => (ex.metadata?.objectif_id || fiche.objectif_id) === o.objectif_id)), version_figee: { ...(fiche.version_figee || {}), empreinte } }
      : null),
    [fiche, empreinte, exercices]
  );

  /**
   * « jamais » | « a-jour » | « modifiee » — ce que l'utilisateur perdrait.
   *
   * L'état suivait jusqu'ici une DATE de dernier enregistrement, qui ne dit
   * rien de ce qui a bougé après. Il suit désormais le CONTENU : revenir à
   * l'état enregistré redit « à jour », parce que c'est vrai.
   */
  const etatEnregistrement = useMemo(() => {
    if (!fiche?.etat?.sheet_uid) return "jamais";
    return fiche.etat.empreinte === empreinte ? "a-jour" : "modifiee";
  }, [fiche, empreinte]);

  /**
   * Abandonner est une DÉCISION, pas un effet de bord.
   *
   * « Changer d'objectif » vidait la fiche, et l'effet de persistance effaçait
   * le brouillon dans la foulée. Un clic, aucune question, rien à récupérer.
   */
  const abandonner = useCallback((options = {}) => {
    if (fiche && !options.confirme && etatEnregistrement !== "a-jour") {
      return { refuse: "TRAVAIL_NON_ENREGISTRE" };
    }
    setFiche(null);
    generationDocument.current += 1;
    setExercices([]);
    setDernierRetrait(null);
    return undefined;
  }, [fiche, etatEnregistrement]);

  return {
    fiche: ficheVue, exercices, questionsExhaustives, brouillonRestaure,
    enCours, positionOccupee, erreur, dernierRetrait, etatEnregistrement,
    erreurEnregistrement, enregistrementEnCours, stockageLocalOK,
    parcoursFerme,
    composer, renouveler, allonger, deplacer, retirer, annulerRetrait,
    renommer, abandonner, enregistrer, rouvrir, retirerObjectif,
    effacerErreur: () => setErreur(null),
  };
}

/** Le corps d'export : la fiche telle qu'elle doit s'imprimer. */
export function corpsExport(fiche, exercices, avecCorrige) {
  return {
    objectif_id: fiche.objectif_id,
    // L'identité de la fiche, telle que le serveur l'a émise. Sans elle, un
    // visiteur sans compte est refusé : la route ne saurait pas quelle fiche
    // compter, et accepterait n'importe quel HTML.
    fiche_jeton: fiche.jeton || null,
    graine: fiche.version_figee?.graine ?? null,
    titre: fiche.titre,
    niveau: fiche.niveau,
    include_correction: !!avecCorrige,
    exercices: exercices.map((ex, i) => ({
      order: i + 1,
      enonce_html: ex.enonce_html || "",
      solution_html: ex.solution_html || "",
      figure_svg_enonce: ex.figure_svg_enonce || ex.figure_svg || null,
      figure_svg_solution: ex.figure_svg_solution || null,
      metadata: ex.metadata || null,
    })),
  };
}

export { API as API_FICHES, entetes as entetesFiche };
